import * as p from '@clack/prompts'
import pc from 'picocolors'

import { ensureVercelAuth } from './auth.js'
import { provisionNeon, provisionNeonInteractive } from './neon.js'
import { createVercelProject } from './project.js'
import { resolveVercelRunner } from './runner.js'
import type { AuthFailureReason, NeonProvisionResult, VercelNeonFlowResult } from './types.js'

/**
 * Top-level orchestrator for the Vercel-backed Neon flow during `betterstart
 * init`: resolve a runner → ensure auth (interactive login link) → create + link
 * a Vercel project → provision Neon and capture the DATABASE_URL.
 *
 * Never throws. Any failure at any step resolves to `{ ok: false }` so the
 * caller can fall back to manual connection-string entry.
 */
export async function runVercelNeonFlow(options: {
  cwd: string
  projectName: string
  interactive: boolean
  plan?: string
  env?: NodeJS.ProcessEnv
}): Promise<VercelNeonFlowResult> {
  const env = options.env ?? process.env

  try {
    const runner = await resolveVercelRunner()

    // Auth may print an interactive login link — keep it outside any spinner.
    const auth = await ensureVercelAuth(runner, options.cwd, { env })
    if (!auth.authed) {
      p.log.warn(authFailureMessage(auth.reason))
      return { ok: false }
    }
    if (auth.username) {
      p.log.success(`Signed in to Vercel as ${pc.cyan(auth.username)}`)
    }

    const spinner = p.spinner()
    spinner.start(`Creating Vercel project ${pc.cyan(options.projectName)}`)
    const project = await createVercelProject(runner, options.cwd, options.projectName, env)

    spinner.message('Provisioning Neon Postgres database')
    let neon = await provisionNeon(runner, options.cwd, { plan: options.plan, env })

    // Terms acceptance / sandbox claim need a human — retry interactively.
    if (
      options.interactive &&
      (neon.failure === 'terms-required' || neon.failure === 'claim-required')
    ) {
      spinner.stop('Confirm the Neon integration in the prompts below')
      neon = await provisionNeonInteractive(runner, options.cwd, { plan: options.plan, env })
      return finishInteractive(neon, project.name, project.projectId)
    }

    if (neon.failure || !neon.databaseUrl) {
      spinner.stop(
        `Vercel provisioning did not complete ${pc.dim(`(${neon.failure ?? 'no database url'})`)}`
      )
      return { ok: false }
    }

    spinner.stop(`Provisioned Neon Postgres and linked ${pc.cyan(project.name)}`)
    return {
      ok: true,
      databaseUrl: neon.databaseUrl,
      vercelProjectId: project.projectId,
      neonResourceName: neon.resourceName
    }
  } catch (error) {
    p.log.warn(
      `Vercel provisioning failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return { ok: false }
  }
}

function finishInteractive(
  neon: NeonProvisionResult,
  projectName: string,
  projectId: string | undefined
): VercelNeonFlowResult {
  if (neon.databaseUrl) {
    p.log.success(`Provisioned Neon Postgres and linked ${pc.cyan(projectName)}`)
    return {
      ok: true,
      databaseUrl: neon.databaseUrl,
      vercelProjectId: projectId,
      neonResourceName: neon.resourceName
    }
  }
  p.log.warn(`Vercel provisioning did not complete (${neon.failure ?? 'no database url'})`)
  return { ok: false }
}

function authFailureMessage(reason?: AuthFailureReason): string {
  switch (reason) {
    case 'login-cancelled':
      return 'Vercel sign-in was cancelled.'
    case 'login-failed':
      return 'Could not verify your Vercel sign-in.'
    case 'timeout':
      return 'Vercel sign-in timed out.'
    case 'no-runner':
      return 'The Vercel CLI is not available.'
    default:
      return 'Could not sign in to Vercel.'
  }
}
